import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertCircle, Download, Rss } from 'lucide-react';
import { FeedHealthBadge } from '../components/FeedHealthBadge';
import { ReportHeader } from '../components/ReportHeader';
import { EventTiles, EventTable } from '../components/EventFeedParts';
import EventSessionsDrawer from '../components/EventSessionsDrawer';

const API = process.env.REACT_APP_BACKEND_URL;
const PER_PAGE = 100;
const UNAVAILABLE_MSG = 'This feed is temporarily unavailable. Please try again shortly.';

const fmt = (n) => (n ?? 0).toLocaleString();
const fmtDT = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '—');
const EMPTY_META = { total: 0, last_page: 1, summary: null, consumer: null, fetched_at: null, pii_allowed: false };

function Tile({ k, label, value, sub }) {
  return (
    <div className="stat-card" data-testid={`feed-tile-${k}`} style={{ padding: 16, minWidth: 0 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', fontWeight: 600 }}>{label}</div>
      <div className="stat-value" title={value} style={{ fontSize: String(value).length > 14 ? 16 : 22, fontWeight: 800, color: '#0f172a', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SummaryTiles({ s, Tile }) {
  if (!s) return null;
  return (
    <div data-testid="feed-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
      <Tile k="enrollments" label="Feed Rows (Enrollments)" value={fmt(s.total_enrollments)} sub={`${fmt(s.unique_learners)} unique learners`} />
      <Tile k="completions" label="Completions" value={fmt(s.total_completions)} sub={`${s.completion_rate}% completion rate`} />
      <Tile k="partners" label="Partners" value={fmt(s.total_partners)} sub={`${fmt(s.total_destination_lms)} destination LMS`} />
      <Tile k="top-partner" label="Top Partner" value={s.top_partner?.name || '—'} sub={s.top_partner ? `${fmt(s.top_partner.enrollments)} enrollments` : null} />
      <Tile k="top-course" label="Top Course" value={s.top_course?.name || '—'} sub={s.top_course ? `${fmt(s.top_course.enrollments)} enrollments · ${fmt(s.total_courses)} courses` : null} />
    </div>
  );
}

function StatusPill({ status }) {
  const done = String(status || '').toLowerCase() === 'completed';
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: done ? '#dcfce7' : '#f1f5f9', color: done ? '#15803d' : '#64748b' }}>
      {status || '—'}
    </span>
  );
}

function Pager({ page, last, total, onPage }) {
  if (last <= 1) return null;
  const from = (page - 1) * PER_PAGE + 1;
  const to = Math.min(page * PER_PAGE, total);
  const pages = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(last, page + 2); i++) pages.push(i);
  return (
    <div className="pagination" data-testid="feed-pagination">
      <span className="pagination-info">{fmt(from)}–{fmt(to)} of {fmt(total)}</span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="feed-pager-prev">‹</button>
      {pages.map(p => <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>)}
      <button className="page-btn" disabled={page >= last} onClick={() => onPage(page + 1)} data-testid="feed-pager-next">›</button>
      <button className="page-btn" disabled={page >= last} onClick={() => onPage(last)}>»</button>
    </div>
  );
}

function FeedTable({ rows }) {
  return (
    <div className="table-scroll">
      <table data-testid="feed-table">
        <thead>
          <tr>
            <th>Learner ID</th><th>Course</th><th>MS Course ID</th><th>Solution Area</th><th>Partner</th>
            <th>Destination LMS</th><th>Micro</th><th>Status</th><th>Enrolled</th><th>Completed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.learner_id}-${r.ms_course_id}-${i}`} data-testid={`feed-row-${i}`}>
              <td style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.learner_id}>{r.learner_id}</td>
              <td style={{ fontSize: 13 }}>{r.course_title || '—'}</td>
              <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.ms_course_id || '—'}</td>
              <td style={{ fontSize: 12 }}>{r.solution_area || '—'}</td>
              <td style={{ fontSize: 12 }}>{r.partner_name || '—'}</td>
              <td style={{ fontSize: 12 }}>{r.destination_lms || '—'}</td>
              <td style={{ fontSize: 12 }}>{r.microskilling ? 'Yes' : 'No'}</td>
              <td><StatusPill status={r.status} /></td>
              <td style={{ fontSize: 12 }}>{fmtDT(r.enrolled_at)}</td>
              <td style={{ fontSize: 12 }}>{fmtDT(r.completed_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const AUDIENCE = { maq: 'MAQ', psh: 'EAS Partner Skilling Hub (PSH)' };
const KIND_CONF = {
  'scorm-cloud-enrollments': {
    titles: { maq: 'SCRMCld for MAQ', psh: 'SCRMCld for PSH' }, noun: 'SCORM Cloud enrollments', file: 'scrmcld',
    defaultSort: 'completed_at:desc', placeholder: 'Course, MS ID, partner, LMS, learner ID…', Tiles: SummaryTiles, Table: FeedTable,
  },
  'event-attendances': {
    titles: { maq: 'EventsUp for MAQ', psh: 'EventsUp for PSH' }, noun: 'EventsUp attendances', file: 'eventsup',
    defaultSort: 'enrolled_at:desc', placeholder: 'Event, session, attendee, company, country…', Tiles: EventTiles, Table: EventTable,
  },
};

export default function FeedMirror({ consumer, kind }) {
  const conf = KIND_CONF[kind];
  const title = conf.titles[consumer];
  const audience = AUDIENCE[consumer];
  const FEED = `${API}/api/feeds/${consumer}/${kind}`;
  const [period, setPeriod] = useState({ startDate: '', endDate: '' });
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState(conf.defaultSort);
  const [applied, setApplied] = useState({ search: '', sort: conf.defaultSort });
  const [drawerEvent, setDrawerEvent] = useState(null);
  const closeDrawer = useCallback(() => setDrawerEvent(null), []);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [exporting, setExporting] = useState(false);
  const handlePeriod = useCallback(({ startDate, endDate }) => setPeriod({ startDate: startDate || '', endDate: endDate || '' }), []);

  const buildParams = useCallback((extra = {}) => {
    const p = { sort: applied.sort, ...extra };
    if (applied.search) p.search = applied.search;
    if (period.startDate) p.date_from = period.startDate;
    if (period.endDate) p.date_to = period.endDate;
    return p;
  }, [applied, period]);

  const fetchPage = useCallback(async (p) => {
    setLoading(true); setErrorMsg(null);
    try {
      const r = await axios.get(FEED, { params: buildParams({ page: p, per_page: PER_PAGE }), withCredentials: true });
      setRows(r.data?.data || []);
      setMeta({ total: r.data?.total || 0, last_page: r.data?.last_page || 1, summary: r.data?.summary, consumer: r.data?.consumer, fetched_at: r.data?.fetched_at, pii_allowed: r.data?.pii_allowed === true });
      setPage(r.data?.current_page || p);
    } catch (e) {
      setErrorMsg(e.response?.status === 403 ? 'This page is restricted to Technofocus users.' : UNAVAILABLE_MSG);
      setRows([]); setMeta(EMPTY_META);
    } finally { setLoading(false); }
  }, [buildParams, FEED]);

  useEffect(() => { fetchPage(1); }, [fetchPage]);

  const apply = () => setApplied({ search: searchInput.trim(), sort });
  const reset = () => { setSearchInput(''); setSort(conf.defaultSort); setApplied({ search: '', sort: conf.defaultSort }); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${FEED}/export`, { params: buildParams(), withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${conf.file}-${consumer}-feed.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed — the feed is temporarily unavailable.');
    } finally { setExporting(false); }
  };

  return (
    <div data-testid={`feed-page-${conf.file}-${consumer}`}>
      <ReportHeader reportTitle={title} onChange={handlePeriod} />
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }} data-testid="feed-title">
            <Rss size={22} color="#4338ca" /> {title}
          </h1>
          <FeedHealthBadge consumer={consumer} kind={kind} />
        </div>
        <p>Live mirror of the {conf.noun} feed delivered by LevelUp to {audience} — exactly the rows they receive. Period filter applies to enrollment date.</p>
        {meta.fetched_at && (
          <div data-testid="feed-meta" style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            Feed consumer: <strong>{meta.consumer || '—'}</strong> · Pulled live {fmtDT(meta.fetched_at)} UTC
          </div>
        )}
      </div>

      <conf.Tiles s={meta.summary} Tile={Tile} />

      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input type="text" className="filter-input" style={{ minWidth: 320 }} placeholder={conf.placeholder} value={searchInput}
            onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} data-testid="feed-search" />
        </div>
        <div className="filter-group">
          <label className="filter-label">Sort</label>
          <select className="filter-select" value={sort} onChange={e => setSort(e.target.value)} data-testid="feed-sort">
            <option value="completed_at:desc">Completed (newest)</option>
            <option value="completed_at:asc">Completed (oldest)</option>
            <option value="enrolled_at:desc">Enrolled (newest)</option>
            <option value="enrolled_at:asc">Enrolled (oldest)</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={apply} data-testid="feed-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={reset} data-testid="feed-reset-btn">Reset</button>
          <button className="btn btn-secondary" onClick={exportCsv} disabled={exporting} data-testid="feed-export-btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : errorMsg ? (
          <div data-testid="feed-error" style={{ display: 'flex', gap: 12, padding: '24px 20px' }}>
            <AlertCircle size={18} color="#f59e0b" /><p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{errorMsg}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty-state" data-testid="feed-empty"><h3>No feed rows found</h3><p>Try adjusting your filters.</p></div>
        ) : (
          <>
            <div className="table-header">
              <span className="table-title">Feed Rows</span>
              <span className="table-count" style={{ marginLeft: 8 }} data-testid="feed-row-count">({fmt(meta.total)} rows)</span>
            </div>
            <conf.Table rows={rows} pii={meta.pii_allowed} onEvent={setDrawerEvent} />
            <Pager page={page} last={meta.last_page} total={meta.total} onPage={fetchPage} />
          </>
        )}
      </div>
      {drawerEvent && <EventSessionsDrawer event={drawerEvent} pii={meta.pii_allowed} onClose={closeDrawer} />}
    </div>
  );
}
